import {
  isActiveDraftComplete,
  isReplyingToFollowUp,
} from './shared-responder-v2-core-follow-up-reply.helpers';
import {
  hasInFlightThread,
  selectCurrentSpec,
  selectCurrentThread,
  selectIsBlockedBySiteGate,
  selectShowsMobileAppEmbed,
} from './shared-responder-v2-core-session.selectors';
import type { ResponderSessionState } from './shared-responder-v2-core-session.types';
import { isOtherTextMissing } from './shared-responder-v2-core-thread-choice';
import { getThreadBehaviour } from './shared-responder-v2-core-thread-registry';
import {
  THREAD_STATUSES,
  TURN_INTENTS,
} from './shared-responder-v2-core-thread.consts';
import { selectIsAwaitingVoiceFollowUpReply } from './shared-responder-v2-core-voice-cluster.selectors';

/**
 * Quant questions never show a text input. One flag on the behaviour, read by
 * both the question surface and the shell composer, so the two cannot drift.
 */
export function selectIsTextInputHidden(state: ResponderSessionState): boolean {
  const thread = selectCurrentThread(state);

  return thread === null || !getThreadBehaviour(thread.kind).acceptsFreeText;
}

export function isRequiredAnswerMissing(state: ResponderSessionState): boolean {
  const spec = selectCurrentSpec(state);
  const thread = selectCurrentThread(state);

  if (spec === null || thread === null || !spec.isRequired) {
    return false;
  }

  if (
    state.editing.mode === 'editing' ||
    thread.answer !== null ||
    thread.status === THREAD_STATUSES.Closed ||
    isReplyingToFollowUp(state, spec.questionId)
  ) {
    return false;
  }

  return !isActiveDraftComplete(state, spec.questionId);
}

/**
 * A required question needs a complete draft before Continue enables; an
 * optional one may otherwise always be skipped, except with Other selected and
 * its text still empty — the pick would be discarded rather than skipped, so
 * this holds ahead of the `isRequired` branch below. The ticket states both
 * "Continue enables only after a response is received" and "non-required
 * questions show Continue so the responder can move on without answering" —
 * these conflict, so the resolution lives here alone and is easy to flip once
 * product rules.
 *
 * The site gate is checked ahead of both, because it is the one rule that binds
 * an optional question too: a task may be skipped without an answer, but not
 * without opening its site.
 */
export function selectIsContinueEnabled(state: ResponderSessionState): boolean {
  const spec = selectCurrentSpec(state);
  const thread = selectCurrentThread(state);

  if (spec === null || thread === null) {
    return false;
  }

  if (selectIsBlockedBySiteGate(state)) {
    return false;
  }

  // A turn already on the wire owns the session. Continue stayed clickable
  // through it, so a double press — or a press while the answer was still
  // sending — fired a second request that the backend could only reject.
  if (hasInFlightThread(state)) {
    return false;
  }

  if (isReplyingToFollowUp(state, spec.questionId)) {
    return (
      selectIsAwaitingVoiceFollowUpReply(state) ||
      isActiveDraftComplete(state, spec.questionId)
    );
  }

  if (isOtherTextMissing(thread.draft)) {
    return false;
  }

  return !isRequiredAnswerMissing(state);
}

export function selectIsMobileAppEmbedYielded(
  state: ResponderSessionState
): boolean {
  const thread = selectCurrentThread(state);

  if (thread === null || !selectShowsMobileAppEmbed(state)) {
    return false;
  }

  return (
    thread.status === THREAD_STATUSES.Failed ||
    thread.turns.some((turn) => turn.intent === TURN_INTENTS.FollowUpQuestion)
  );
}

export function selectIsLiveDistributionOffered(
  state: ResponderSessionState,
  questionId: string
): boolean {
  const step = state.steps.find((entry) => entry.questionId === questionId);
  const thread = state.threads[questionId];

  if (step === undefined || thread === undefined) {
    return false;
  }

  return (
    step.showLiveDistribution &&
    getThreadBehaviour(thread.kind).supportsLiveDistribution
  );
}

/**
 * Whether this question has a tally the responder is entitled to see at all —
 * everything `selectIsDistributionVisible` asks except the responder's own
 * show/hide choice, so the surface can offer a "show results" control while the
 * rows are hidden instead of dead-ending on the hide press.
 *
 * Live results are keyed off the per-question `showLiveDistribution` setting and
 * never off the project-level `isPoll` flag, which is frozen at publish time and
 * therefore unreliable after an edit.
 */
export function selectIsDistributionAvailable(
  state: ResponderSessionState,
  questionId: string
): boolean {
  return (
    selectIsLiveDistributionOffered(state, questionId) &&
    (state.threads[questionId]?.answer ?? null) !== null
  );
}

export function selectIsDistributionVisible(
  state: ResponderSessionState,
  questionId: string
): boolean {
  return (
    selectIsDistributionAvailable(state, questionId) &&
    (state.threads[questionId]?.isDistributionVisible ?? false)
  );
}
